import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { HttpError, useDataProvider, useNotify, useTranslate } from 'react-admin'

import { isRunInProgress, type SourceRun, type SourceRunSource, type SourceRunStatus } from '@/data/source'
import type { ScalesDataProvider } from '@/dataProvider'
import { formatDateTime } from '@/domain/dateTime'

const SOURCE_RUN_POLL_MS = 3000

const STATUS_COLORS: Record<SourceRunStatus, 'default' | 'info' | 'success' | 'error'> = {
  REQUESTED: 'default',
  RUNNING: 'info',
  SUCCEEDED: 'success',
  FAILED: 'error',
}

type Notice = { kind: 'busy'; since: string } | { kind: 'refused'; message: string }

export interface CountLabel {
  name: string
  label: string
}

interface RunSummaryProps {
  run: SourceRun
  testId: (part: string) => string
  countLabels?: CountLabel[]
}

const RunSummary: FC<RunSummaryProps> = ({ run, testId, countLabels }) => {
  const translate = useTranslate()
  const counts = countLabels
    ? countLabels.flatMap(({ name, label }) => (run.counts[name] === undefined ? [] : [[translate(label), run.counts[name]] as const]))
    : Object.entries(run.counts)
  return (
    <Stack spacing={1} data-testid={testId('last-run')}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">
          {translate('pages.hidden.run.last_run', { trigger: translate(`pages.hidden.run.trigger.${run.trigger}`) })}
        </Typography>
        <Chip
          size="small"
          color={STATUS_COLORS[run.status]}
          label={translate(`pages.hidden.run.status.${run.status}`)}
          data-testid={testId('run-status')}
        />
      </Stack>
      <Typography variant="body2">
        {translate('pages.hidden.run.requested_at')}: {formatDateTime(run.requestedAt)}
      </Typography>
      <Typography variant="body2" data-testid={testId('run-finished')}>
        {translate('pages.hidden.run.finished_at')}: {run.finishedAt === null ? '—' : formatDateTime(run.finishedAt)}
      </Typography>
      {counts.length > 0 && (
        <Typography variant="body2" data-testid={testId('run-counts')}>
          {translate('pages.hidden.run.counts')}: {counts.map(([name, count]) => `${name}: ${String(count)}`).join(' · ')}
        </Typography>
      )}
      {run.problems.length > 0 && (
        <Alert severity="error" data-testid={testId('run-problems')}>
          {translate('pages.hidden.run.problems')}
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {run.problems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </Box>
        </Alert>
      )}
    </Stack>
  )
}

interface SourceReadPanelProps {
  source: SourceRunSource
  countLabels?: CountLabel[]
}

export const SourceReadPanel: FC<SourceReadPanelProps> = ({ source, countLabels }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const dataProvider = useDataProvider<ScalesDataProvider>()
  const [run, setRun] = useState<SourceRun | null | undefined>(undefined)
  const [starting, setStarting] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  const labels = `pages.hidden.${source.toLowerCase()}`
  const testId = (part: string) => `${source.toLowerCase()}-${part}`

  useEffect(() => {
    const read = { cancelled: false }
    void (async () => {
      try {
        const latest = await dataProvider.latestRun(source)
        if (!read.cancelled) setRun(latest ?? null)
      } catch (error) {
        notify(error instanceof Error ? error.message : String(error), { type: 'error' })
      }
    })()
    return () => {
      read.cancelled = true
    }
  }, [dataProvider, notify, source])

  const followedId = run && isRunInProgress(run) ? run.id : null
  useEffect(() => {
    if (followedId === null) return
    const poll = { cancelled: false }
    const timer = setInterval(() => {
      void (async () => {
        const latest = await dataProvider.getSourceRun(followedId)
        if (!poll.cancelled && latest) setRun(latest)
      })()
    }, SOURCE_RUN_POLL_MS)
    return () => {
      poll.cancelled = true
      clearInterval(timer)
    }
  }, [dataProvider, followedId])

  const start = async () => {
    setStarting(true)
    setNotice(null)
    try {
      const started = await dataProvider.startRead(source)
      if (started.kind === 'busy') setNotice({ kind: 'busy', since: started.run.requestedAt })
      setRun((await dataProvider.getSourceRun(started.run.id)) ?? null)
    } catch (error) {
      if (error instanceof HttpError && error.status === 502) {
        setNotice({ kind: 'refused', message: String(error.message) })
        setRun((await dataProvider.latestRun(source)) ?? null)
      } else {
        notify(error instanceof Error ? error.message : String(error), { type: 'error' })
      }
    } finally {
      setStarting(false)
    }
  }

  const inProgress = followedId !== null
  return (
    <Card variant="outlined" data-testid={testId('sync')}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">{translate(`${labels}.title`)}</Typography>
          <Box>
            <Button
              variant="contained"
              disabled={starting || inProgress || run === undefined}
              onClick={() => void start()}
              data-testid={testId('sync-button')}
            >
              {run && inProgress
                ? translate(`${labels}.in_progress`, { status: translate(`pages.hidden.run.status.${run.status}`) })
                : translate(`${labels}.action`)}
            </Button>
          </Box>
          {notice?.kind === 'busy' && (
            <Alert severity="info" data-testid={testId('sync-busy')}>
              {translate(`${labels}.busy`, { since: formatDateTime(notice.since) })}
            </Alert>
          )}
          {notice?.kind === 'refused' && (
            <Alert severity="error" data-testid={testId('sync-refused')}>
              {translate(`${labels}.refused`, { message: notice.message })}
            </Alert>
          )}
          {run === null && <Typography variant="body2">{translate(`${labels}.never`)}</Typography>}
          {run && <RunSummary run={run} testId={testId} countLabels={countLabels} />}
        </Stack>
      </CardContent>
    </Card>
  )
}
